// Minimal shared type definitions to support pricing calculations across frontend and worker
export interface PricingOptionChoiceLike {
    id: string;
    extraCost?: number;
}

export interface PricingProductOptionLike {
    id: string;
    name?: string;
    choices?: PricingOptionChoiceLike[];
}

export interface PricingProductLike {
    id: string;
    name?: string;
    price: number;
    options?: PricingProductOptionLike[];
}

export interface PricingSelectedOptionLike {
    optionId: string;
    choiceIds: string[];
}

export interface PricingOrderItemLike {
    productId: string;
    quantity: number;
    selectedOptions?: PricingSelectedOptionLike[];
}

export interface CalculatedOrderItem {
    productId: string;
    productName?: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
    selectedOptions?: PricingSelectedOptionLike[];
}

export interface CalculateOrderTotalsResult {
    items: CalculatedOrderItem[];
    subtotal: number;
    errors: string[];
}

export const roundCurrency = (value: number): number => {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Number(value.toFixed(2));
};

export const calculateItemUnitPrice = (
    product: PricingProductLike,
    selectedOptions: PricingSelectedOptionLike[] = []
): { unitPrice: number; errors: string[] } => {
    let unitPrice = Number(product.price) || 0;
    const errors: string[] = [];

    if (!Array.isArray(selectedOptions) || selectedOptions.length === 0) {
        return { unitPrice: roundCurrency(unitPrice), errors };
    }

    const optionMap = new Map(
        (product.options || []).map(option => [option.id, option])
    );

    selectedOptions.forEach(selectedOption => {
        const productOption = optionMap.get(selectedOption.optionId);
        if (!productOption) {
            errors.push(`Unknown option '${selectedOption.optionId}' for product '${product.name || product.id}'.`);
            return;
        }

        if (!Array.isArray(selectedOption.choiceIds)) {
            errors.push(`Invalid choices for option '${selectedOption.optionId}'.`);
            return;
        }

        const choiceMap = new Map(
            (productOption.choices || []).map(choice => [choice.id, choice])
        );

        selectedOption.choiceIds.forEach(choiceId => {
            const choice = choiceMap.get(choiceId);
            if (!choice) {
                errors.push(`Unknown choice '${choiceId}' for option '${selectedOption.optionId}'.`);
                return;
            }
            const extra = Number(choice.extraCost) || 0;
            unitPrice += extra;
        });
    });

    return { unitPrice: roundCurrency(unitPrice), errors };
};

export const calculateOrderTotals = (
    products: PricingProductLike[],
    items: PricingOrderItemLike[]
): CalculateOrderTotalsResult => {
    const errors: string[] = [];
    const calculatedItems: CalculatedOrderItem[] = [];
    let subtotal = 0;

    const productMap = new Map(products.map(product => [product.id, product]));

    items.forEach((item, index) => {
        const quantity = Number(item.quantity);
        if (!Number.isFinite(quantity) || quantity <= 0) {
            errors.push(`Invalid quantity for item at position ${index + 1}.`);
            return;
        }

        const product = productMap.get(item.productId);
        if (!product) {
            errors.push(`Product '${item.productId}' not found.`);
            return;
        }

        const { unitPrice, errors: priceErrors } = calculateItemUnitPrice(
            product,
            item.selectedOptions || []
        );

        if (priceErrors.length > 0) {
            errors.push(...priceErrors);
        }

        const lineTotal = roundCurrency(unitPrice * quantity);
        subtotal += lineTotal;

        calculatedItems.push({
            productId: product.id,
            productName: product.name,
            quantity,
            unitPrice,
            lineTotal,
            selectedOptions: item.selectedOptions || []
        });
    });

    return {
        items: calculatedItems,
        subtotal: roundCurrency(subtotal),
        errors
    };
};

export const calculateCartTotal = (items: CalculatedOrderItem[]): number => {
    const total = items.reduce((sum, item) => sum + item.lineTotal, 0);
    return roundCurrency(total);
};

/**
 * Calculate display price with discount applied
 * Returns both original and final price for strikethrough display
 */
export interface DisplayPriceResult {
    currentPrice: number;
    originalPrice: number | null;
    hasDiscount: boolean;
}

export const calculateDisplayPrice = (
    basePrice: number,
    discountPercentage?: number
): DisplayPriceResult => {
    const normalizedBasePrice = Number(basePrice) || 0;

    if (!discountPercentage || discountPercentage <= 0) {
        return {
            currentPrice: roundCurrency(normalizedBasePrice),
            originalPrice: null,
            hasDiscount: false
        };
    }

    const discountRatio = 1 - (discountPercentage / 100);
    const discountedPrice = normalizedBasePrice * discountRatio;

    return {
        currentPrice: roundCurrency(discountedPrice),
        originalPrice: roundCurrency(normalizedBasePrice),
        hasDiscount: true
    };
};
